import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { ArrowLeft, ClipboardList, ChevronRight, AlertCircle } from 'lucide-react';

interface SurveyListItem {
  id: string;
  title: string;
  description?: string;
  type?: string;
  end_date?: string;
  question_count?: string | number;
}

/**
 * Lista as pesquisas ativas da empresa (janela de datas vigente) para o promotor
 * escolher qual responder durante a visita. Ponto de entrada que faltava — sem ele
 * a tela Survey ficava órfã (audit 03/07 achado #6 / TP-4).
 */
export default function SurveyList({
  visitId,
  storeId,
}: {
  visitId?: string;
  storeId: string;
}) {
  const [, setLocation] = useLocation();

  // Com visita (fluxo merchandising) volta p/ a visita; avulsa (Pesquisa PDV) p/ escolha de loja.
  const backTo = visitId ? `/visit/${storeId}` : '/pesquisa-pdv';
  const surveyHref = (survey: SurveyListItem) => {
    // A survey oficial (TP-004) usa a tela dedicada de sortimento+preço por
    // categoria, não o fluxo genérico de perguntas — ver PesquisaPdv.tsx.
    // Identificada pelo título (mesmo critério do backend/mobile em geral,
    // não há flag dedicada em surveys).
    if (survey.title.includes('Pesquisa Oficial')) {
      return visitId ? `/pesquisa-oficial/${storeId}/${visitId}` : `/pesquisa-oficial/${storeId}`;
    }
    return visitId
      ? `/survey/${survey.id}/${visitId}/${storeId}`
      : `/survey/${survey.id}/loja/${storeId}`;
  };

  const { data, isLoading, isError, refetch } = useQuery<SurveyListItem[]>({
    queryKey: ['surveys', 'active'],
    queryFn: () =>
      api.get('/surveys?status=active&limit=50').then((res) => res.data.data),
  });

  const surveys = data ?? [];

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation(backTo)}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
            aria-label="Voltar"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Pesquisas</h1>
            <p className="text-sm text-white/80">Selecione a pesquisa para responder</p>
          </div>
        </div>
      </header>

      <div className="flex-1 p-4 space-y-3">
        {isLoading && (
          <>
            {[0, 1].map((i) => (
              <div
                key={i}
                className="h-20 rounded-xl bg-gray-200 animate-pulse"
                aria-hidden
              />
            ))}
          </>
        )}

        {isError && !isLoading && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <AlertCircle className="h-10 w-10 text-red-500" />
            <p className="text-gray-700">Não foi possível carregar as pesquisas.</p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!isLoading && !isError && surveys.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <ClipboardList className="h-10 w-10 text-gray-400" />
            <p className="text-gray-700 font-medium">Nenhuma pesquisa disponível</p>
            <p className="text-sm text-gray-500 max-w-xs">
              Não há pesquisas ativas para responder no momento.
            </p>
          </div>
        )}

        {!isLoading &&
          !isError &&
          surveys.map((survey) => (
            <button
              key={survey.id}
              onClick={() => setLocation(surveyHref(survey))}
              className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50 text-left"
            >
              <div className="p-3 rounded-full bg-blue-100 flex-shrink-0">
                <ClipboardList className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{survey.title}</p>
                <p className="text-sm text-gray-500 truncate">
                  {survey.description ||
                    `${survey.question_count ?? 0} pergunta(s)`}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
            </button>
          ))}
      </div>
    </div>
  );
}
